/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/ProposalsListView/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { IconButton, SvgIcon } from "@mui/material";
import styles from "./index.module.css";
import RejectProposalView from "../ProposalRejectionView/index.jsx";
import ProposalDetailsView from "../ProposalDetailsView/index.jsx";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";
import { ReactComponent as RequirementIcon } from "../../assets/icons/Requirement.svg";
import { ReactComponent as CategoryIcon } from "../../assets/icons/ProposalsListModal/RequirementCategoryIcon.svg";
import {
  sortProposalsByDestacado,
  filterProposals,
  getCategoryTitle,
} from "../../logic/proposal/index.js";
import { useTranslation } from "react-i18next";
import { useProposalsListView } from "./useProposalsListView.js";
import ProposalsListSection from "./ProposalsListSection.jsx";
import ProposalsListRequirementSummary from "./ProposalsListRequirementSummary.jsx";

const ProposalsListView = ({ requirement, onBack }) => {
  const { t } = useTranslation();
  const {
    proposals,
    categories,
    showRejectModal,
    showDisplayModal,
    selectedProposal,
    acceptedExpanded,
    rejectedExpanded,
    isActive,
    onProposalAction,
    onRejectSubmit,
    onToggleActive,
    handleViewProposal,
    handleModalClose,
    toggleAcceptedExpanded,
    toggleRejectedExpanded,
  } = useProposalsListView(requirement);

  if (showDisplayModal) {
    return (
      <ProposalDetailsView
        onBack={() => handleModalClose("display")}
        proposal={selectedProposal}
        requirement={requirement}
      />
    );
  }

  if (showRejectModal) {
    return (
      <RejectProposalView
        onBack={() => handleModalClose("reject")}
        proposal={selectedProposal}
        onSend={onRejectSubmit}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.backNavigation}>
          <IconButton onClick={onBack}>
            <ChevronLeftIcon />
          </IconButton>
          <span className={styles.mainTitle}>{requirement.titulo}</span>
        </div>
        <div className={styles.statusItem}>
          <SvgIcon
            sx={{
              fontSize: "14px",
              color: requirement.activo ? "#00E864" : "red",
            }}
            component={RequirementIcon}
          />
          <label className={styles.modalSubtitle}>
            {requirement.activo
              ? t("components.requirementDisplayModal.activeRequirement")
              : t("components.requirementDisplayModal.inactiveRequirement")}
          </label>
        </div>
        <div className={styles.tagsContainer}>
          <div className={styles.categoryTag}>
            <CategoryIcon />
            <span>
              {getCategoryTitle(categories, requirement?.idCategoria)}
            </span>
          </div>
          <div className={styles.categoryTag}>
            <CategoryIcon />
            <span>{requirement.nombreSubcategoria}</span>
          </div>
        </div>
      </div>

      <ProposalsListRequirementSummary
        requirement={requirement}
        isActive={isActive}
        onToggleActive={onToggleActive}
        t={t}
        styles={styles}
      />

      <div className={styles.proposals}>
        <div className={styles.mainTitle}>
          {t("components.proposalsListModal.proposals")}
        </div>
        <div className={styles.proposalsList}>
          <ProposalsListSection
            filteredProposals={filterProposals.pending(proposals)}
            sectionType="pending"
            sortProposalsByDestacado={sortProposalsByDestacado}
            onProposalAction={onProposalAction}
            onViewProposal={handleViewProposal}
            t={t}
            styles={styles}
          />
        </div>

        <div className={styles.proposalsListContainer}>
          <div className={styles.title} onClick={toggleAcceptedExpanded}>
            {t("components.proposalsListModal.accepted")}
            <KeyboardArrowDownIcon
              className={`${styles.arrowIcon} ${
                acceptedExpanded ? "" : styles.rotated
              }`}
            />
          </div>
          <div
            className={`${styles.divider} ${
              !acceptedExpanded ? styles.hidden : ""
            }`}
          ></div>
          <div
            className={`${styles.proposalsList} ${styles.collapsible} ${
              acceptedExpanded ? styles.expanded : ""
            }`}
          >
            <ProposalsListSection
              filteredProposals={filterProposals.accepted(proposals)}
              sectionType="accepted"
              sortProposalsByDestacado={sortProposalsByDestacado}
              onProposalAction={onProposalAction}
              onViewProposal={handleViewProposal}
              t={t}
              styles={styles}
            />
          </div>
        </div>

        <div className={styles.proposalsListContainer}>
          <div className={styles.title} onClick={toggleRejectedExpanded}>
            {t("components.proposalsListModal.rejected")}
            <KeyboardArrowDownIcon
              className={`${styles.arrowIcon} ${
                rejectedExpanded ? "" : styles.rotated
              }`}
            />
          </div>
          <div
            className={`${styles.divider} ${
              !rejectedExpanded ? styles.hidden : ""
            }`}
          ></div>
          <div
            className={`${styles.proposalsList} ${styles.collapsible} ${
              rejectedExpanded ? styles.expanded : ""
            }`}
          >
            <ProposalsListSection
              filteredProposals={filterProposals.rejected(proposals)}
              sectionType="rejected"
              sortProposalsByDestacado={sortProposalsByDestacado}
              onProposalAction={onProposalAction}
              onViewProposal={handleViewProposal}
              t={t}
              styles={styles}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalsListView;
